import { useState, useEffect, createContext, useContext } from 'react';
import api from '../services/api';

interface ConfigState {
    [key: string]: any;
}

interface ConfigContextType {
    config: ConfigState;
    isLoading: boolean;
    refreshConfig: () => Promise<void>;
}

// Default fallback configuration (Production Safeguard)
const DEFAULT_CONFIG: ConfigState = {
    theme_primary_color: '#F59E0B',
    theme_secondary_color: '#111827',
    maintenance_mode: false,
    hero_title: 'Your AI Home Finder'
};

export const ConfigContext = createContext<ConfigContextType>({
    config: DEFAULT_CONFIG,
    isLoading: true,
    refreshConfig: async () => {},
});

export const useConfig = () => useContext(ConfigContext);

export const useRemoteConfigDriver = () => {
    const [config, setConfig] = useState<ConfigState>(DEFAULT_CONFIG);
    const [isLoading, setIsLoading] = useState(true);

    const applyTheme = (cfg: ConfigState) => {
        const root = document.documentElement;
        if (cfg.theme_primary_color) {
            root.style.setProperty('--color-brand', cfg.theme_primary_color);
            // Also set a tailwind-like variable if needed by custom plugins, 
            // though usually this requires style attribute injection or class mapping.
            // For now, simpler CSS variable overrides:
        }
        // Add more dynamic styling logic here
    };

    const fetchConfig = async () => {
        try {
            // Using the legacy /api/config/public path registered in main.py
            const response = await api.get(`/api/v1/config/public?t=${new Date().getTime()}`);
            const data = response.data;
            
            // Only merge if response is a valid object (not HTML string from SPA fallback)
            if (data && typeof data === 'object' && !Array.isArray(data) && typeof data !== 'string') {
                const finalConfig = { ...DEFAULT_CONFIG, ...data };
                setConfig(finalConfig);
                applyTheme(finalConfig);
            } else {
                console.warn("Config API returned invalid data, using defaults");
                applyTheme(DEFAULT_CONFIG);
            }
        } catch (error) {
            console.error("Failed to load remote config, using defaults", error);
            // We keep the defaults we initialized with
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    return { config, isLoading, refreshConfig: fetchConfig };
};
